import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

import { AuthorizedCpaUserCpaData } from '@liscio/api';
import { CpaStateItem } from '@liscio/common';

import { config } from 'config';

export const APP_CPA = 'application_cpa';
export const NATIVE_LAST_LOGGED_CPA = 'native_last_logged_cpa';
export const NON_CPA_SUBDOMAINS = ['app', 'firm'];

export const getSavedApplicationCpa = async () => {
  try {
    const { value: cpaData } = await SecureStoragePlugin.get({ key: APP_CPA });
    const cpa = JSON.parse(cpaData) as CpaStateItem;
    return cpa;
  } catch {
    // fail silently
  }
};

export const saveApplicationCpa = async (cpa: CpaStateItem) => {
  try {
    await SecureStoragePlugin.set({ key: APP_CPA, value: JSON.stringify(cpa) });
  } catch {
    // fail silently
  }
};

export const clearApplicationCpa = async () => {
  try {
    await SecureStoragePlugin.remove({ key: APP_CPA });
  } catch {
    // fail silently
  }
};

/**
 * Resolve cpa site url from location.
 * Falls back to dev env var when available.
 */
export const getCpaSiteUrl = (options: { hostnameOnly?: boolean } = {}) => {
  // Fallback to dev defined CPA in preview environments
  // NOTE: This should only be used in dev/preview envs
  const origin = config.dev.defaultSiteCpa || window?.location?.origin;
  const siteUrl = new URL(origin);

  // Only return CPA for web platforms with defined cpa subdomain
  if (!Capacitor.isNativePlatform() && siteUrl.hostname !== 'localhost') {
    return options.hostnameOnly ? siteUrl.hostname : siteUrl.origin;
  }
};

export const makeStateCpaFromV5Cpa = (
  v5Cpa: AuthorizedCpaUserCpaData
): CpaStateItem => ({
  id: v5Cpa.cpa_user_id,
  name: v5Cpa.cpa_name,
  logo: v5Cpa.cpa_logo_url,
  custom_liscio_url: v5Cpa.company_url,
  cpa_uuid: v5Cpa.cpa_uuid,
  is_current_cpa: false,
  is_active: v5Cpa.is_active,
  cpa_user_role: v5Cpa.cpa_user_role,
});

const parseFirmUrl = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;

  try {
    return new URL(trimmed);
  } catch {
    try {
      return new URL(`https://${trimmed}`);
    } catch {
      return undefined;
    }
  }
};

const isFrontendOwnedCpaSwitchRoute = (pathname: string) =>
  pathname === '/dashboard' || pathname.startsWith('/dashboard/');

export const buildPostCpaSwitchRedirectUrl = (cpa: CpaStateItem) => {
  const firmUrl = parseFirmUrl(cpa.custom_liscio_url);
  if (!firmUrl || !['http:', 'https:'].includes(firmUrl.protocol)) {
    return cpa.custom_liscio_url || '';
  }

  const redirectOrigin = isFrontendOwnedCpaSwitchRoute(window.location.pathname)
    ? window.location.origin
    : firmUrl.origin;
  const redirectUrl = new URL(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
    redirectOrigin
  );
  const companyName = firmUrl.hostname.toLowerCase();

  if (companyName) {
    redirectUrl.searchParams.set('company_name', companyName);
  }
  if (cpa.cpa_uuid) {
    redirectUrl.searchParams.set('cpa_uuid', cpa.cpa_uuid);
  }
  // `redirectedFrom` means "the other app's origin" — DesktopSiteRedirect
  // stores it as the legacy redirect host in non-production. A same-origin
  // value poisons that host and causes a reload loop on legacy routes, so
  // drop any inherited value and only send it on cross-origin redirects.
  redirectUrl.searchParams.delete('redirectedFrom');
  if (
    config.environment !== 'production' &&
    redirectUrl.origin !== window.location.origin
  ) {
    redirectUrl.searchParams.set(
      'redirectedFrom',
      `${window.location.protocol}//${window.location.host}`
    );
  }

  return redirectUrl.toString();
};

/**
 * Normalize a firm host/url for company_name comparison: trims padding,
 * strips the scheme and trailing slashes, and lowercases, so
 * ' https://Foo.liscio.me/ ' and 'foo.liscio.me' compare equal. Returns
 * undefined for blank (including whitespace-only) input so it can never
 * accidentally equal a real host.
 */
export const normalizeCompanyHost = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed
    ? trimmed
        .replace(/^https?:\/\//i, '')
        .replace(/\/+$/, '')
        .toLowerCase()
    : undefined;
};
